/**
 * Um evento da linha do tempo: circulo com icone, conector vertical, hora,
 * distancia ate o marco zero ("T+2h49"), titulo e detalhe. Casos com
 * conteudo proprio: a ligacao com gravacao mostra o `AudioPlayer` (RNF17,
 * exclusividade garantida pelo provedor da lista) e a resposta do lead sai
 * como citacao, para o texto dele nao se confundir com o texto do motor.
 *
 * Dois selos explicam a regra de negocio sem abrir o ADR: "Contato" marca
 * a tentativa que chegou ao telefone (abre a janela de 7 dias) e "Contato
 * creditado" marca o ultimo contato antes do pagamento -- o que recebe a
 * comissao.
 *
 * `local` marca o que foi registrado nesta previa (venda pela vendedora,
 * remocao da regua): recebe o selo "Registrado na prévia" para nunca parecer
 * dado vindo do servidor.
 */

import { AudioPlayer } from '../../../components/AudioPlayer';
import { Badge, Icon } from '../../../components/ui';
import { formatDateTime } from '../../../lib/format';
import type { FunnelKind } from '../../../types';
import { contaComoContato } from '../resumo';
import type { TimelineEvent } from '../types';
import { descreverEvento, descreverRelativo, posicaoRelativaAoFato, TOM_DO_ICONE } from '../vocabulario';

export function ItemDaLinha({
  event,
  local,
  agora,
  funnel,
  fatoIso,
  creditado,
}: {
  readonly event: TimelineEvent;
  readonly local: boolean;
  readonly agora: Date;
  readonly funnel: FunnelKind;
  readonly fatoIso: string;
  readonly creditado: boolean;
}): JSX.Element {
  const d = descreverEvento(event);
  const contato = contaComoContato(event);
  return (
    <li className={`group relative flex gap-4 ${creditado ? '-mx-2 rounded-xl bg-status-good/5 px-2' : ''}`}>
      <div className="flex flex-col items-center">
        <span aria-hidden="true" className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border ${TOM_DO_ICONE[d.tone]}`}>
          <Icon name={d.icon} size="sm" />
        </span>
        <span aria-hidden="true" className="mt-1 w-px flex-1 bg-line group-last:hidden" />
      </div>

      <div className="min-w-0 flex-1 pb-6 group-last:pb-0">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-subtle">
          <time dateTime={event.at} className="tabular-nums">
            {formatDateTime(event.at)}
          </time>
          <span>· {descreverRelativo(event.at, agora)}</span>
          {local ? null : (
            <span className="rounded-md border border-line bg-page px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-ink-muted" title="Distância até o marco zero (abandono ou vencimento)">
              {posicaoRelativaAoFato(funnel, fatoIso, event.at)}
            </span>
          )}
          {creditado ? (
            <Badge tone="good" icon="check">
              Contato creditado
            </Badge>
          ) : contato ? (
            <span className="rounded-full border border-line px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle" title="Chegou ao telefone: abre a janela de 7 dias para a comissão (ADR-001)">
              Contato
            </span>
          ) : null}
          {local ? <Badge tone="accent">Registrado na prévia</Badge> : null}
        </p>
        <p className="mt-0.5 text-sm font-medium text-ink">{d.title}</p>
        {d.detail === null ? null : <p className="mt-0.5 text-sm text-ink-muted">{d.detail}</p>}

        {event.kind === 'lead_reply' ? (
          <blockquote className="mt-2 rounded-xl border border-series-7/30 bg-series-7/5 px-3 py-2 text-sm text-ink">“{event.text}”</blockquote>
        ) : null}

        {event.kind === 'voice_call' && event.recordingUrl !== null ? (
          <div className="mt-3 rounded-xl border border-line bg-surface px-3 py-2">
            <AudioPlayer id={event.id} src={event.recordingUrl} label={`gravação da ${event.attempt}ª ligação da IA`} fallbackDurationSeconds={event.durationSeconds} />
          </div>
        ) : null}
      </div>
    </li>
  );
}
